import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { IPublicClientApplication } from '@azure/msal-browser/dist/app/IPublicClientApplication';
import { loginRequest } from '../authConfig';
import { publicClientApplication } from '../components/AuthWrapper/AuthWrapper';

async function acquireAccessTokenFromMsal(
  forceRefresh: boolean
): Promise<string> {
  const account = publicClientApplication.getActiveAccount();

  if (account === null)
    throw new Error(
      'Access token cannot be acquired as there is no active account.'
    );

  const request = {
    scopes: loginRequest.scopes,
    account,
    forceRefresh,
  };

  try {
    const result = await publicClientApplication.acquireTokenSilent(request);
    return result.idToken;
  } catch {
    try {
      const redirectPromise =
        await publicClientApplication.handleRedirectPromise();
      if (redirectPromise !== null) return redirectPromise.idToken;

      const result = await publicClientApplication.acquireTokenSilent(request);
      return result.idToken;
    } catch {
      throw new Error('Unable to acquire access token');
    }
  }
}

let inFlightTokenRequest: Promise<string> | null = null;
let inFlightWasForced = false;

// Multiple requests can 401 at the same moment (e.g. several API calls firing
// together after the cached token expires). Without this, each one would kick
// off its own MSAL silent token request in parallel, which can trip AAD
// throttling or race on MSAL's own token cache. Share a single in-flight
// refresh across all of them instead.
//
// forceRefresh matters because the API validates the ID token, but MSAL
// decides whether to refresh based on the ACCESS token's expiry — and AAD
// gives those different lifetimes (ID ~60min fixed, access 60-90min random).
// In the mismatch window acquireTokenSilent happily returns the same expired
// ID token from cache, so a 401 retry must bypass the cache.
export function acquireAccessToken(forceRefresh = false): Promise<string> {
  if (inFlightTokenRequest && (inFlightWasForced || !forceRefresh))
    return inFlightTokenRequest;

  inFlightWasForced = forceRefresh;
  inFlightTokenRequest = acquireAccessTokenFromMsal(forceRefresh).finally(
    () => {
      inFlightTokenRequest = null;
    }
  );

  return inFlightTokenRequest;
}

export const retrieveAccessToken = () => sessionStorage.getItem('accessToken');

export const storeAccessToken = (token: string) =>
  sessionStorage.setItem('accessToken', token);

export const clearAccessToken = () => sessionStorage.removeItem('accessToken');

export async function logoutOf(account: AccountInfo) {
  try {
    clearAccessToken();
    sessionStorage.clear();

    await publicClientApplication.logoutRedirect({
      account,
      postLogoutRedirectUri: '/',
    });
  } catch (e) {
    console.error(e);
  }
}

export async function logout() {
  const account = publicClientApplication.getActiveAccount();
  if (account) await logoutOf(account);
  else throw new Error('Unexpected Account Error - Active Account Undefined');
}

export async function login() {
  try {
    await publicClientApplication.loginRedirect(loginRequest);
  } catch (e) {
    console.error(e);
  }
}

function handleResponse(response: AuthenticationResult | null) {
  console.log(['handle response', response]);
  if (response) {
    publicClientApplication.setActiveAccount(response.account);

    acquireAccessToken()
      .then(storeAccessToken)
      .catch((error) => {
        throw new Error(error);
      });
  }
}

publicClientApplication.handleRedirectPromise().then(handleResponse);

export enum UserRole {
  Reporter = 'Reporter',
  FCP = 'FCP',
  OT = 'OT',
  Podiatrist = 'Podiatrist',
  AP = 'AP',
}

export const hasRole = (
  instance: IPublicClientApplication,
  role: UserRole
): boolean => {
  const account = instance.getActiveAccount();
  if (!account) return false;

  const claims = account.idTokenClaims;
  if (!claims) return false;

  const roles = claims.roles ?? [];

  return roles.indexOf(role) !== -1;
};
